import { EP } from "../performance/PerfomanceItem";
import Logger from "../Logger";
import ProjectMetadata from "../models/ProjectMetadata";
import BaseStorageHandler from "./BaseStorageHandler";
import getLocalStorageHandler from "./LocalstorageHandler";
import { activeAutobackupIdsToDelete } from "./SnapshotRetention";
import type {
  AutobackupItemRecord,
  SaveAutobackupRequest,
  SaveAutobackupResponse,
} from "./AutobackupWorkerMessages";

const DELETED_IDS = "deletedIds"; // no itx

const logger = new Logger();

/**
 * Collects the project data and lets a worker store a regular local ZIP copy.
 */
export default class AutobackupStorageHandler implements BaseStorageHandler {
  get isLocalStorgeHandle(): boolean {
    return true;
  }

  private readonly localStorageHandler = getLocalStorageHandler();
  private projectId = "";
  private pendingItems = new Map<string, AutobackupItemRecord>();
  private worker: Worker | undefined;
  private workerRequests = new Map<string, (response: SaveAutobackupResponse) => void>();

  async ensureDbOpen(): Promise<boolean> {
    return this.localStorageHandler.ensureDbOpen();
  }

  saveData = async (item: any): Promise<boolean> => {
    if (!this.projectId) return false;

    this.pendingItems.set(item.id, {
      projectId: this.projectId,
      itemId: item.id,
      parentId: item.parentId,
      data: item,
    });
    return true;
  };

  private async collectProjectItems(projectId: string, showErrors: boolean): Promise<boolean> {
    if (!(await this.ensureDbOpen())) return false;
    this.projectId = projectId;
    this.pendingItems.clear();

    let serialized = false;
    await EP("saveProjectItems.collect").runAsync(async () => {
      serialized = await state.saveToEstadaStorage(this.saveData, showErrors, false);
    });
    if (!serialized) return false;

    this.pendingItems.set(DELETED_IDS, {
      projectId,
      itemId: DELETED_IDS,
      data: [...state.objectRegistry.deletedIds],
    });
    return true;
  }

  private getWorker(): Worker {
    if (this.worker) return this.worker;

    this.worker = new Worker(new URL("./Autobackup.worker.ts", import.meta.url), {
      type: "module",
      name: "estada-autobackup",
    });
    this.worker.onmessage = (event: MessageEvent<SaveAutobackupResponse>) => {
      const callback = this.workerRequests.get(event.data.requestId);
      if (!callback) return;
      this.workerRequests.delete(event.data.requestId);
      callback(event.data);
    };
    this.worker.onerror = (event) => {
      logger.error("Autobackup worker failed", event.message);
      this.workerRequests.forEach((callback, requestId) => {
        callback({ type: "saved", requestId, success: false, error: event.message });
      });
      this.workerRequests.clear();
      this.worker?.terminate();
      this.worker = undefined;
    };
    return this.worker;
  }

  private saveInWorker(request: Omit<SaveAutobackupRequest, "type" | "requestId">): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const requestId = crypto.randomUUID();
      this.workerRequests.set(requestId, (response) => {
        if (!response.success) logger.error("Autobackup worker could not save project", response.error);
        resolve(response.success);
      });

      try {
        this.getWorker().postMessage({ type: "save", requestId, ...request } satisfies SaveAutobackupRequest);
      } catch (error) {
        this.workerRequests.delete(requestId);
        logger.error("Could not send project to autobackup worker", error);
        resolve(false);
      }
    });
  }

  private sendToWorker(
    request: Omit<SaveAutobackupRequest, "type" | "requestId">,
    onComplete: (response: SaveAutobackupResponse) => void,
  ): boolean {
    const requestId = crypto.randomUUID();
    this.workerRequests.set(requestId, (response) => {
      if (!response.success) logger.error("Autobackup worker could not save project", response.error);
      onComplete(response);
    });

    try {
      this.getWorker().postMessage({ type: "save", requestId, ...request } satisfies SaveAutobackupRequest);
      return true;
    } catch (error) {
      this.workerRequests.delete(requestId);
      logger.error("Could not send project to autobackup worker", error);
      return false;
    }
  }

  /** Starts an autobackup and returns as soon as the worker message was sent. */
  async startProjectBackup(
    projectId: string,
    metadata: ProjectMetadata,
    showErrors: boolean,
    onComplete: (success: boolean) => void,
  ): Promise<boolean> {
    try {
      if (!(await this.collectProjectItems(projectId, showErrors))) return false;
      const backupDate = new Date();
      const autobackupId = `${projectId}-snapshot-autobackup-${backupDate.toISOString()}`;
      const persistentMetadata = metadata.toJSON();
      persistentMetadata.mainId = projectId;
      persistentMetadata.id = autobackupId;
      persistentMetadata.localCopied = backupDate.toISOString();
      persistentMetadata.lastModifiedLocal = undefined;

      let sent = false;
      await EP("saveProjectItems.sendToWorker").runAsync(async () => {
        sent = this.sendToWorker(
          {
            projectId: autobackupId,
            items: [...this.pendingItems.values()],
            metadata: persistentMetadata,
          },
          (response) => onComplete(response.success),
        );
      });
      return sent;
    } catch (error) {
      logger.error("Error while starting autobackup", error);
      return false;
    } finally {
      this.pendingItems.clear();
    }
  }

  /** Saves project items and metadata in one worker-side transaction. */
  async saveProject(projectId: string, metadata: ProjectMetadata, showErrors = true): Promise<boolean> {
    try {
      if (!(await this.collectProjectItems(projectId, showErrors))) return false;
      const persistentMetadata = metadata.toJSON();

      let stored = false;
      await EP("saveProjectItems.saveInWorker").runAsync(async () => {
        stored = await this.saveInWorker({
          projectId,
          items: [...this.pendingItems.values()],
          metadata: { ...persistentMetadata, id: projectId },
        });
      });
      return stored;
    } catch (error) {
      logger.error("Error while saving autobackup", error);
      return false;
    } finally {
      this.pendingItems.clear();
    }
  }

  /** Saves the complete current project without creating a ZIP or Blob. */
  async saveProjectItems(projectId: string, showErrors = true): Promise<boolean> {
    try {
      if (!(await this.collectProjectItems(projectId, showErrors))) return false;
      return await this.saveInWorker({ projectId, items: [...this.pendingItems.values()] });
    } finally {
      this.pendingItems.clear();
    }
  }

  /** Loads the complete project directly from IndexedDB. */
  async loadProjectItemsFromIndexedDb(projectId: string): Promise<boolean> {
    return (await this.loadProjectItems(projectId)) !== undefined;
  }

  async deleteProjectItems(projectId: string): Promise<boolean> {
    return this.localStorageHandler.deleteProjectItems(projectId);
  }

  /** Deletes autobackups created after the project's latest manual snapshot. */
  async deleteActiveAutobackups(projectId: string): Promise<boolean> {
    try {
      const projects = await this.localStorageHandler.loadProjectList(state.loggedInUser.email);
      const idsToDelete = activeAutobackupIdsToDelete(projects, projectId);
      const results = await Promise.all(
        idsToDelete.map(async (id) => {
          const itemsDeleted = await this.localStorageHandler.deleteProjectItems(id);
          const metadataDeleted = await this.localStorageHandler.deleteProjectMetadata(id);
          return itemsDeleted && metadataDeleted;
        }),
      );
      return results.every(Boolean);
    } catch (error) {
      logger.error("Could not delete active autobackups", error);
      return false;
    }
  }

  async saveProjectMetadata(projectId: string, data: ProjectMetadata): Promise<boolean> {
    if (!(await this.ensureDbOpen())) return false;
    const persistentData = typeof data?.toJSON === "function" ? data.toJSON() : data;
    return this.saveInWorker({ projectId, metadata: { ...persistentData, id: projectId } });
  }

  async loadProjectMetadata(projectId: string): Promise<any> {
    return this.localStorageHandler.loadProjectMetadata(projectId);
  }

  async deleteProjectMetadata(projectId: string): Promise<boolean> {
    return this.localStorageHandler.deleteProjectMetadata(projectId);
  }

  async loadProjectList(user: String): Promise<any> {
    return this.localStorageHandler.loadProjectList(user.toString());
  }

  // PDF, installation-check and authentication APIs are not part of the
  // autobackup workflow.
  async saveProjectItems2(projectId: string, blob: Blob): Promise<boolean> {
    return this.localStorageHandler.saveProjectItems2(projectId, blob);
  }

  async loadProjectItemsBlob(projectId: string): Promise<Blob | undefined> {
    return this.localStorageHandler.loadProjectItemsBlob(projectId);
  }

  async loadProjectItems(projectId: string): Promise<Blob | undefined> {
    return this.localStorageHandler.loadProjectItems(projectId);
  }

  async userIsLoggedIn(): Promise<boolean> {
    return true;
  }

  async login(params: { email: string; password: string }): Promise<any | undefined> {
    return params;
  }

  async loadPdfFile(_id: string): Promise<Blob | undefined> {
    return undefined;
  }

  async savePdfFile(_id: string, _blob: Blob): Promise<boolean> {
    return false;
  }

  async saveInstallationChecks(_projectId: string, _ownerId: string, _item: any): Promise<boolean> {
    return false;
  }

  async loadInstallationChecks(_projectId: string, _ownerId: string): Promise<any | undefined> {
    return undefined;
  }
}
